<template>
  <div class="order-message-attachment"
       :title="datetime">
    <a class="order-message-attachment__file"
       target="_blank"
       v-if="!isImage && ownMessage"
       :href="attachment.url">
      <i class="ns-icon-ico014"></i>
      <div class="message">
        <p>You sent a file</p>
        <p class="attachment-datetime">{{ fileDateTimeString }}</p>
      </div>
    </a>
    <div v-if="!isImage && !ownMessage"
         class="order-message-attachment__file-received">
      <p class="m-b-10">Sent a file</p>
      <a class="order-message-attachment__file"
        target="_blank"
        :href="attachment.url">
        <i class="ns-icon-ico014"></i>
        <div class="message">
          <p>File</p>
        </div>
      </a>
      <p class="time m-t-10">{{ formattedTime }}</p>
    </div>
    <a v-if="isImage"
       :href="attachment.url"
       :style="{backgroundImage: `url(${attachment.url})`}"
       class="order-message-attachment__image"
       target="_blank">
      <div v-if="previewLoading" class="loading-spinner">
        <smd-loading-spinner/>
      </div>
      <p v-if="error" class="error">Preview not found.</p>
      <div class="message">
        <p v-if="message.msg">{{ message.msg }}</p>
        <p class="time">{{ formattedTime }}</p>
      </div>
    </a>
  </div>
</template>

<script>
  import {MessageService} from '@/services/messages';
  import {formatDate} from '@/utils/dates';
  import {MIME_TYPES} from '@/constants/inbox-messages';

  export default {
    name: 'order-message-attachment',
    props: {
      message: {type: Object, required: true},
    },
    data() {
      return {
        error: null,
        attachment: {},
        previewLoading: false,
      };
    },
    computed: {
      datetime() {
        return formatDate(this.message.date, 'MMMM D, YYYY hh:mm a');
      },
      isImage() {
        const imageTypes = [MIME_TYPES.png, MIME_TYPES.jpg, MIME_TYPES.jpeg];
        return imageTypes.includes(this.message.mimetype);
      },
      formattedTime() {
        return formatDate(this.message.date, 'h:mm a');
      },
      ownMessage() {
        return this.message.from === '1';
      },
      fileExtension() {
        const fileExtension = this.message.uploadid.split('.');
        return fileExtension.pop();
      },
      fileDateTimeString() {
        return this.isImage ? null : formatDate(this.message.date, '[Sent on] MMM D, YYYY h:mm a');
      },
    },
    mounted() {
      if (this.isImage) {
        this.previewLoading = true;
        this.fetchFile();
      }
    },
    methods: {
      async goToFile() {
        if (this.isImage) {
          window.open(this.attachment.url, '_blank');
        } else {
          this.fetchFile();
        }
      },
      async fetchFile() {
        this.error = null;

        try {
          const {data} = await MessageService.getOrderAttachmentFile(this.message.orderid, this.message.uploadid);

          if (!data) {
            throw Error();
          }

          this.previewLoading = false;
          this.attachment = data;
        } catch (e) {
          this.previewLoading = false;
          this.error = 'An unexpected error occured on fetching the image.';
          this.$emit('file-img-error', e);
        }
      },
    }
  }
</script>

<style lang="scss">
  .order-message-attachment {
    &__file {
      display: flex;
      align-items: center;
      padding: space(s) space(m);
      border-radius: radius(medium);
      color: inherit;

      &:not(.received) {
        @include box-shadow($shadow);
      }

      i {
        font-size: font(xxl);
        margin-right: space(s);
        color: color(gray, text);
      }

      .message {
        p:first-child {
          color: color(base, text);
          font-weight: font(bold);
        }

        .attachment-datetime {
          font-size: font(s);
          color: color(label, text);
        }
      }
    }

    &__file-received {
      width: 100%;
      min-width: 200px;
      max-width: 200px;
      padding: space(s) space(m);
      border-radius: radius(medium);
      background-color: color(lightGray, background);

      .time {
        font-size: font(s);
        text-align: right;
        color: color(label, text);
      }
    }

    &__image {
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      display: block;
      overflow: hidden;
      border-radius: radius(medium);
      height: 250px;
      width: 150px;
      position: relative;

      .loading-spinner {
        padding: space(s);
      }

      .error {
        text-align: center;
        padding: space(s);
        color: color(red, brand);
      }

      .message {
        position: absolute;
        bottom: 0;
        width: 100%;
        color: color(white, text);
        padding: space(xs) space(m);
        background-color: color(blue, brand);

        .time {
          font-size: font(s);
          text-align: right;
        }
      }
    }
  }
</style>
